import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dumbbell, LogOut, Loader2, Trash2, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'tceo_admin_token';

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMembers = async (authToken) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/members`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setMembers(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error('Sessió caducada, torna a iniciar sessió');
      } else {
        toast.error('Error carregant els socis');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchMembers(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success('Benvingut, administrador!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error d\'inici de sessió');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMembers([]);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/members/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers((m) => m.filter((x) => x.id !== id));
      toast.success('Soci eliminat');
    } catch (err) {
      toast.error('No s\'ha pogut eliminar');
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-gray-900 border-2 border-[#39FF14]/40 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <Dumbbell className="w-8 h-8 text-[#39FF14]" />
            <div>
              <div className="text-[#39FF14] font-black text-xl">TRAINING</div>
              <div className="text-white text-xs">Panell d'administració</div>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="admin-email" className="text-gray-300">Correu</Label>
              <Input
                id="admin-email"
                type="email"
                data-testid="admin-email-input"
                value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-gray-300">Contrasenya</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#2EDD0F] text-black font-bold px-6 py-3 rounded-full transition-all disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              Iniciar sessió
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-[#39FF14]" />
            <h1 className="text-3xl font-black">Socis <span className="text-[#39FF14]">registrats</span></h1>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 border border-gray-700 hover:border-[#39FF14] text-white px-4 py-2 rounded-full transition-all"
          >
            <LogOut className="w-4 h-4" /> Sortir
          </button>
        </div>

        <div className="mb-4 text-gray-400" data-testid="members-count">
          Total: <span className="text-[#39FF14] font-bold">{members.length}</span> socis
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#39FF14]" /></div>
        ) : members.length === 0 ? (
          <div className="text-center text-gray-500 py-20" data-testid="members-empty">Encara no hi ha socis registrats.</div>
        ) : (
          <div className="rounded-xl border border-gray-800 overflow-x-auto">
            <Table data-testid="members-table">
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-gray-400">Nom</TableHead>
                  <TableHead className="text-gray-400">Correu</TableHead>
                  <TableHead className="text-gray-400">Telèfon</TableHead>
                  <TableHead className="text-gray-400">Quota</TableHead>
                  <TableHead className="text-gray-400">Data</TableHead>
                  <TableHead className="text-gray-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} className="border-gray-800" data-testid={`member-row-${m.id}`}>
                    <TableCell className="font-medium text-white">{m.name}</TableCell>
                    <TableCell className="text-gray-300">{m.email}</TableCell>
                    <TableCell className="text-gray-300">{m.phone}</TableCell>
                    <TableCell className="text-gray-300">{m.plan}</TableCell>
                    <TableCell className="text-gray-500 text-sm">{new Date(m.created_at).toLocaleDateString('ca-ES')}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleDelete(m.id)}
                        data-testid={`delete-member-${m.id}`}
                        className="text-red-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
